import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { Blocks } from "@/components/Blocks";
import { PageHeader, Spinner, Markdown } from "@/components/common";
import { useMeta } from "@/hooks/useMeta";
import { postMeta } from "@/pages/Blog";
import { ShareButton } from "@/components/ShareButton";
import { LikeButton, cid } from "@/components/LikeButton";
import { useReadTracking } from "@/lib/useReadTracking";
import { AuthorBox, RelatedPosts, SideNewsletter, PostFooter } from "@/components/BlogPostParts";
import { fmtDate } from "@/pages/Blog";

export function NotFound() {
  const loc = useLocation();
  const nav = useNavigate();
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    api.get("/redirects/resolve", { params: { path: loc.pathname, ref: document.referrer } }).then((r) => {
      const to: string = r.data.to_path;
      if (!to) return setChecked(true);
      const dest = to.includes("?") || !loc.search ? to : to + loc.search;
      if (/^https?:\/\//.test(dest)) window.location.replace(dest); else nav(dest + (loc.hash || ""), { replace: true });
    }).catch(() => setChecked(true));
  }, [loc.pathname]);
  useMeta(checked ? "Page not found | Somnio.Co" : undefined, undefined, null, { noindex: true });
  if (!checked) return <Spinner />;
  return (
    <div className="max-w-xl mx-auto text-center px-5 py-32" data-testid="not-found-page">
      <p className="eyebrow text-[var(--c-primary)]">404</p>
      <h1 className="font-display text-5xl font-light mt-4">This page has moved on</h1>
      <Link to="/" className="btn-ink inline-block mt-10 px-9 py-4 text-xs tracking-[0.25em] uppercase">Return Home</Link>
    </div>
  );
}

export function CmsPage({ slug: fixed }: { slug?: string }) {
  const params = useParams();
  const slug = fixed || params.slug;
  const [p, setP] = useState<any>(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => { setP(null); setMissing(false); api.get(`/pages/${slug}`).then((r) => setP(r.data)).catch(() => setMissing(true)); }, [slug]);
  useMeta(p?.seo_title || p?.title, p?.seo_description || p?.excerpt, null, { image: p?.image || p?.blocks?.find((b: any) => typeof b.image === "string" && b.image)?.image, noindex: !!p?.noindex });
  if (missing) return <NotFound />;
  if (!p) return <Spinner />;
  const first = p.blocks?.[0];
  const full = ["banner", "vip_hero", "edu_hero", "giveaway"].includes(first?.type) || (first?.type === "intro" && first?.shaded);
  return (
    <article data-testid="cms-page">
      {!full && <PageHeader eyebrow={p.kind === "education" ? "Education" : undefined} title={p.title} intro={p.excerpt} />}
      {p.image && !full && <div className="max-w-[1400px] mx-auto px-5 md:px-12 mb-10"><img src={p.image} alt="" className="w-full max-h-[60vh] object-cover" /></div>}
      <Blocks blocks={p.blocks} slug={p.slug} />
    </article>
  );
}

export function EducationIndex() {
  const [pages, setPages] = useState<any[] | null>(null);
  useMeta("Diamond & Jewelry Education | Somnio.Co", "Guides to the 4Cs, lab-grown diamonds, ring sizing and precious metals.");
  useEffect(() => { api.get("/pages", { params: { kind: "education" } }).then((r) => setPages(r.data)); }, []);
  return (
    <div data-testid="education-page">
      <PageHeader eyebrow="Learn" title="Education" intro="Everything you need to choose with confidence." />
      {!pages ? <Spinner /> : (
        <div className="max-w-[1400px] mx-auto px-5 md:px-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {pages.map((p) => (
            <Link key={p.id} to={`/education/${p.slug}`} className="group" data-testid={`education-card-${p.slug}`}>
              <div className="aspect-[4/5] overflow-hidden bg-[var(--c-muted)]">{p.image && <img src={p.image} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}</div>
              <p className="font-display text-2xl mt-4 group-hover:text-[var(--c-primary)]">{p.title}</p>
              <p className="text-sm text-neutral-600 mt-1">{p.excerpt}</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [p, setP] = useState<any>(null);
  const [missing, setMissing] = useState(false);
  const [params] = useSearchParams();
  const preview = params.get("preview") || "";
  useEffect(() => { api.get(`/blog/${slug}`, { params: preview ? { preview } : {} }).then((r) => setP(r.data)).catch(() => setMissing(true)); }, [slug, preview]);
  useEffect(() => { if (p?.slug && !p.preview) api.post(`/blog/${p.slug}/view`, { cid: cid() }).catch(() => {}); }, [p?.slug, p?.preview]);
  useReadTracking(p?.slug, !!p && !p.preview);
  useMeta(p?.seo_title || p?.title, p?.seo_description || p?.excerpt,
    p ? { "@context": "https://schema.org", "@type": "BlogPosting", headline: p.title, datePublished: p.date, image: p.image, author: { "@type": "Organization", name: p.author } } : null,
    { image: p?.image, noindex: !!p?.noindex || !!p?.preview, type: "article" });
  if (missing) return <NotFound />;
  if (!p) return <Spinner />;
  return (
    <>
    {p.preview && (
      <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-center text-xs tracking-[0.12em] uppercase py-3 px-4" data-testid="post-preview-banner">
        Preview — this article isn't live yet{p.publish_at_local ? ` · goes live ${p.publish_at_local.replace("T", " ")} Sydney time` : p.published === false ? " · currently hidden" : ""}
      </div>
    )}
    <article className="max-w-[1400px] mx-auto px-5 md:px-12 pt-6 pb-20 grid lg:grid-cols-[minmax(0,1fr)_320px] gap-12 lg:gap-20" data-testid="blog-post-page">
      <div className="min-w-0">
        <nav className="text-[11px] text-neutral-500" aria-label="Breadcrumb" data-testid="post-breadcrumb"><Link to="/" className="hover:underline">Somnio.Co</Link> | <Link to="/blog" className="hover:underline">Blog</Link> | <span>The Journal</span></nav>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight mt-4" data-testid="post-title">{p.title}</h1>
        <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2">
          <p className="text-[11px] tracking-[0.14em] uppercase text-neutral-600" data-testid="post-date">{fmtDate(p.date)}{p.category ? ` · ${p.category}` : ""} · {Math.max(1, Math.round((p.body || "").split(/\s+/).length / 200))} min read</p>
          <LikeButton slug={p.slug} likes={p.likes || 0} />
          <ShareButton path={`/blog/${p.slug}`} title={p.title} label className="text-[11px] tracking-[0.14em] uppercase hover:text-[var(--c-primary)] transition-colors" />
        </div>
        <img src={p.image} alt={p.title} className="mt-8 w-full aspect-[16/9] object-cover bg-neutral-100" data-testid="post-cover" />
        <div className="mt-10 max-w-none" data-testid="post-body"><Markdown text={p.body} /></div>
        <div className="mt-10 flex items-center gap-3 text-sm text-neutral-600" data-testid="post-like-cta"><LikeButton slug={p.slug} likes={p.likes || 0} /><span>Enjoyed this story? Give it a like.</span></div>
        <PostFooter p={p} />
      </div>
      <aside className="space-y-12 lg:sticky lg:top-32 self-start" data-testid="post-sidebar">
        <AuthorBox p={p} />
        <RelatedPosts items={p.related} />
        <SideNewsletter />
      </aside>
    </article>
    </>
  );
}

export function FaqPage() {
  const [faqs, setFaqs] = useState<any[] | null>(null);
  useEffect(() => { api.get("/faqs").then((r) => setFaqs(r.data)); }, []);
  useMeta("FAQ | Somnio.Co", "Answers to common questions about ordering, shipping, returns, sizing and payments.",
    faqs ? { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })) } : null);
  const groups = (faqs || []).reduce((a: any, f: any) => { (a[f.category] = a[f.category] || []).push(f); return a; }, {});
  return (
    <div data-testid="faq-page">
      <PageHeader eyebrow="Help" title="Frequently Asked Questions" />
      {!faqs ? <Spinner /> : (
        <div className="max-w-[1400px] mx-auto px-5 md:px-12"><div className="max-w-3xl space-y-12">
          {Object.entries(groups).map(([cat, items]: any) => (
            <section key={cat}>
              <p className="eyebrow text-[var(--c-primary)] mb-3">{cat}</p>
              <Accordion type="single" collapsible>
                {items.map((f: any) => (
                  <AccordionItem key={f.id} value={f.id}><AccordionTrigger className="font-display text-lg text-left" data-testid={`faq-q-${f.id}`}>{f.question}</AccordionTrigger><AccordionContent className="text-neutral-600 leading-relaxed">{f.answer}</AccordionContent></AccordionItem>
                ))}
              </Accordion>
            </section>
          ))}
        </div></div>
      )}
    </div>
  );
}
